import React, { useState, useRef } from 'react';
import {Row, Col, Input, Button, Table, notification, Select} from 'antd';
import { useTranslation } from 'react-i18next';
import { camelizerHelper } from '../../../../../../../helpers';
import MallaPropiedadEditor from './MallaPropiedadEditor';
import { updateMallaPropiedadPromise } from '../promises'

const MallaPropiedad = ({record, details, forceExpand, itemTable, currentUser, onOpenTree, onOpenCertificate, onSaved}) => {

    const { t } = useTranslation()
    const [editingMalla, setEditingMalla] = useState(false)
    const [tempMalla, setTempMalla] = useState([])
    const [saving, setSaving] = useState(false)
    const validateMallaRef = useRef(null)

    const getMallaPropiedad = (ubos) => {
        if (!Array.isArray(ubos)) return []
        return ubos.filter(u => u.origenMallaPropiedad === 'CLIENTE')
    }

    const malla = getMallaPropiedad(details?.entity?.propietarios)

    const canEdit =
        record?.ddaStatus === 'ENTREGADO' &&
        record?.form?.status !== 'VALIDADO' &&
        currentUser?.type !== 'AUDIT' &&
        !editingMalla

    const handleEnterEdit = () => {
        setTempMalla(cloneMalla(malla))
        setEditingMalla(true)
    }

    const handleCancel = () => {
        setTempMalla([])
        setEditingMalla(false)
    }

    const handleChangeUbo = (index, field, value) => {
        setTempMalla(prev => {
            const updatedUbo = [...prev]
            updatedUbo[index] = { ...updatedUbo[index], [field]: value }
            return updatedUbo
        })
    }

    const handleAddUbo = () => {
        setTempMalla(prev => [...prev, emptyUbo()])
    }

    const handleRemoveUbo = (index) => {
        if (tempMalla.length <= 1) {
            notification.warning({
                message: t('messages.aml.attention'),
                description: t('messages.aml.mallaMinimoUnUbo')
            })
            return
        }

        const closeConfirm = () => notification.close('confirm-remove-ubo')

        const confirmRemove = () => {
            setTempMalla(prev => prev.filter((actual, i) => i !== index))
            closeConfirm()
        }

        const btn = (
            <div className="message-buttons-confirm">
                <Button type="ghost" onClick={closeConfirm}>{t('messages.aml.btnClose')}</Button>&nbsp;
                <Button type="primary" onClick={confirmRemove}>{t('messages.aml.accept')}</Button>
            </div>
        )

        notification.info({
            key: 'confirm-remove-ubo',
            message: t('messages.aml.confirm'),
            description: t('messages.aml.confirmEliminarUbo'),
            duration: 10,
            btn
        })
    }

    //Validación basado en relacionados - validateFields
    const setValidateMalla = (validateFn) => {
        validateMallaRef.current = validateFn
    }

    const handleSave = () => {
        if (!validateMallaRef.current) return

        validateMallaRef.current(async (err) => {
            if (err) {

                let errorMsg= t('messages.aml.notifications.anErrorOcurred');

                Object.keys(err).forEach(key => {
                    const lastUnderscore = key.lastIndexOf('_')
                    if (lastUnderscore === -1) return
                    const index = parseInt(key.substring(lastUnderscore + 1), 10)
                    if (isNaN(index)) return
                    errorMsg = err[key].errors[0].message
                })
                notification.error({
                    message: t('messages.aml.notifications.anErrorOcurred'),
                    description: errorMsg
                })
                return
            }
            setSaving(true)
            const result = await saveMalla({ idPJ: itemTable.id, tempMalla })
            setSaving(false)
            if (result.ok) {
                notification.success({
                    message: t('messages.aml.attention'),
                    description: t('messages.aml.mallaActualizada')
                })
                setEditingMalla(false)
                setTempMalla([])
                if (onSaved) onSaved(true)
            } else {
                notification.error({
                    message: t('messages.aml.notifications.anErrorOcurred'),
                    description: result.message || t('messages.aml.mallaErrorActualizar')
                })
            }
        })
    }

    const cloneMalla = (malla) => {
        if (!malla) return []
        return malla.map(registro => ({
            rut: registro.rut,
            name: registro.name,
            participacionDirecta: registro.participacionDirecta,
            paisId: registro.paisId != null ? Number(registro.paisId) : (registro.pais === 'NACIONAL' ? 1 : 2),
            pais: registro.pais,
            nacionalidad: registro.nacionalidad,
            ubo: registro.ubo,
            error: registro.error
        }))
    }

    const emptyUbo = () => ({
        rut: '',
        name: '',
        participacionDirecta: null,
        paisId: 1,
        pais: 'NACIONAL',
        nacionalidad: null,
        ubo: false,
        error: null
    })


    const saveMalla = async ({ idPJ, tempMalla }) => {
        const payload = {
            ubos: tempMalla.map(u => ({
                rut: u.rut,
                name: u.name,
                participacionDirecta: u.participacionDirecta,
                paisId: u.paisId
            }))
        }

        try {
            const response = await updateMallaPropiedadPromise(idPJ, payload.ubos)
            return { ok: response?.status === 'OK', status: response?.status, message: response?.message, detail: response?.detail }
        } catch (e) {
            return { ok: false, status: 'NETWORK_ERROR' }
        }
    }

    const readOnlyColumns = [
        {
            title: t('messages.aml.rut'),
            dataIndex: 'rut',
            render: (text) => {return text }
        },
        {
            title: t('messages.aml.name'),
            dataIndex: 'name',
            render: (text) => {
                const formatted = camelizerHelper(text)
                return (
                    <span className="nombre-truncate" title={formatted}>
                {formatted}
            </span>
                )
            },
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: t('messages.aml.participation'),
            dataIndex: 'participacionDirecta',
            render: (text) => {
                if (text !== null) {
                    return (Math.round(text * 100)/100) + '%'
                }
            },
            sorter: (a, b) => a.participacionDirecta - b.participacionDirecta
        },
        {
            title: t('messages.aml.citizenship'),
            dataIndex: 'nacionalidad',
            render: (text, record) => {
                return record.pais ==="NACIONAL"? t('messages.aml.national'): t('messages.aml.foreign')}

        },
        {
            title: t('messages.aml.ubo'),
            dataIndex: 'ubo',
            render: (text) => text ? t("messages.aml.yes") : t("messages.aml.no")
        },
        {
            title: t('messages.aml.differences'),
            dataIndex: 'error',
            whiteSpace: 'nowrap',
            render: (error) => {
                if(error !== undefined && error !== null) {
                    return {
                        props: {
                            className: 'error-' + error.code,
                        },
                        children: <div className="nowrap">{ t('messages.aml.malla.error' + error.code) }</div>                    }
                }
            }
        }
    ]

    return (
        <div className='mallas-propiedad-main-wrapper'>
            <Row gutter={[50]}>
                <Col span={6}>
                    {t("messages.aml.status")}
                    <Input value={t('messages.aml.malla.' + record?.ddaStatus)} readOnly/>
                </Col>
                <Col span={6}>
                    {t("messages.aml.malla.receptionDate")}
                    <Input value={record?.strDdaDate} readOnly/>
                </Col>
            </Row>

            <Row style={{marginTop:20}}>
                <Col span={6}>
                    {t("messages.aml.finalBeneficiaries")}
                </Col>
                <Col span={18} style={{textAlign: 'right'}}>
                    {record?.ddaStatus === 'ENTREGADO' && !editingMalla &&
                        <Button onClick={onOpenTree} style={{ marginRight: 8 }}>{t("messages.aml.viewGrid")}</Button>
                    }
                    {!editingMalla && <Button icon="file-pdf" onClick={onOpenCertificate} style={{ marginRight: 8 }}>{t("messages.aml.certificate")}</Button>
                    }
                    {canEdit && <Button icon="edit" onClick={handleEnterEdit}>{t("messages.aml.edit")}</Button>
                    }
                </Col>
            </Row>

            { editingMalla ?
                <MallaPropiedadEditor
                    tempMalla={tempMalla}
                    saving={saving}
                    currentUser={currentUser}
                    onChangeUbo={handleChangeUbo}
                    onAddUbo={handleAddUbo}
                    onRemoveUbo={handleRemoveUbo}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    forceExpand={forceExpand}
                    setValidateMalla={setValidateMalla}
                />
                :
                <Row style={{marginTop:20}}>
                    <Col span={24}>
                        { details?.entity?.propietarios?.length > 0 ?
                            <Table dataSource={malla}
                                   columns={readOnlyColumns}
                                   pagination={!forceExpand && malla?.length > 10} size='small'/>
                            :
                            <>
                                {record?.ddaStatus === 'ENTREGADO' ?
                                    <h3 className="no-info-red">{t('messages.aml.noInfoPossibleAboutFinalBeneficiaries')}</h3>
                                    :
                                    <h3 className="no-info">{t('messages.aml.noInfoAboutFinalBeneficiaries')}</h3>
                                }
                            </>
                        }
                    </Col>
                </Row>
            }
        </div>
    )
}

export default MallaPropiedad;
